import type { UserProfileResponse } from '@/api/types';
import { ROLES, type Role } from '@/constants/roles';

export type Action =
  | 'analytics.view'
  | 'team.members.view'
  | 'team.members.edit'
  | 'team.settings.edit'
  | 'team.settings.editAll'
  | 'webhooks.manage'
  | 'extraction.debug'
  | 'extraction.configure'
  | 'admin.models.manage'
  | 'admin.dev-panel'
  | 'jobs.model.view'
  | 'jobs.feedback.view'
  | 'jobs.delete';

const hasRole = (u: UserProfileResponse, role: Role): boolean => u.roles?.includes(role) ?? false;

const hasAnyRole = (u: UserProfileResponse, ...roles: Role[]): boolean =>
  roles.some((r) => hasRole(u, r));

export const POLICIES: Record<Action, (u: UserProfileResponse) => boolean> = {
  'analytics.view': (u) => hasRole(u, ROLES.ADMIN),
  'team.settings.editAll': (u) => hasRole(u, ROLES.ADMIN),
  'extraction.debug': (u) => hasRole(u, ROLES.ADMIN),
  'extraction.configure': (u) => hasRole(u, ROLES.ADMIN),
  'admin.models.manage': (u) => hasRole(u, ROLES.ADMIN),
  'admin.dev-panel': (u) => hasRole(u, ROLES.ADMIN),
  'jobs.model.view': (u) => hasRole(u, ROLES.ADMIN),
  'jobs.feedback.view': (u) => hasRole(u, ROLES.ADMIN),

  'team.members.view': (u) => hasAnyRole(u, ROLES.ADMIN, ROLES.TEAM_ADMIN),
  'team.members.edit': (u) => hasAnyRole(u, ROLES.ADMIN, ROLES.TEAM_ADMIN),
  'team.settings.edit': (u) => hasAnyRole(u, ROLES.ADMIN, ROLES.TEAM_ADMIN),
  'webhooks.manage': (u) => hasAnyRole(u, ROLES.ADMIN, ROLES.TEAM_ADMIN),
  'jobs.delete': (u) => hasAnyRole(u, ROLES.ADMIN, ROLES.TEAM_ADMIN),
};

// Capabilities the dev-panel override must never touch — admin.dev-panel forced
// off would hide the panel's own entry point. Consulted by useCan (gate) and the
// override UI (which hides these), so both stay in agreement from one source.
export const NON_OVERRIDABLE_ACTIONS: readonly Action[] = ['admin.dev-panel'];

// Pure form for use in non-hook contexts (tests, post-hoc checks)
export function can(action: Action, profile: UserProfileResponse): boolean {
  return POLICIES[action](profile);
}
